import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useDropzone } from "react-dropzone";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Scan, Sparkles, RefreshCw, Check, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Vehicle, VehicleInsert } from "@/hooks/useVehicles";
import { VEHICLE_CATEGORIES, FUEL_TYPES, ACQUISITION_TYPES } from "@/hooks/useVehicles";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";

const vehicleSchema = z.object({
  license_plate: z.string().min(1, "Immatriculation requise"),
  brand: z.string().min(1, "Marque requise"),
  model: z.string().min(1, "Modèle requis"),
  category: z.enum(["vl", "vu", "pl", "two_wheels", "other"]),
  fuel_type: z.enum(["gasoline", "diesel", "electric", "hybrid", "lpg", "other"]).nullable(),
  acquisition_type: z.enum(["owned", "leasing", "loa", "lld", "rental"]).nullable(),
  first_registration_date: z.string().nullable(),
  acquisition_date: z.string().nullable(),
  vin: z.string().nullable(),
  notes: z.string().nullable(),
});

type VehicleFormValues = z.infer<typeof vehicleSchema>;
type ScanState = "idle" | "scanning" | "success" | "error";

interface VehicleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle?: Vehicle | null;
  onSubmit: (data: VehicleInsert) => Promise<void>;
}

export function VehicleDialog({ open, onOpenChange, vehicle, onSubmit }: VehicleDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanState, setScanState] = useState<ScanState>("idle");
  const [scannedFile, setScannedFile] = useState<File | null>(null);
  const [uploadedFilePath, setUploadedFilePath] = useState<string | null>(null);
  const [saveDocument, setSaveDocument] = useState(true);
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());
  const { openDocument } = useDocumentPreview();

  const form = useForm<VehicleFormValues>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: {
      license_plate: "",
      brand: "",
      model: "",
      category: "vl",
      fuel_type: "gasoline",
      acquisition_type: "owned",
      first_registration_date: null,
      acquisition_date: null,
      vin: null,
      notes: null,
    },
  });

  useEffect(() => {
    if (vehicle) {
      form.reset({
        license_plate: vehicle.license_plate,
        brand: vehicle.brand,
        model: vehicle.model,
        category: vehicle.category,
        fuel_type: vehicle.fuel_type,
        acquisition_type: vehicle.acquisition_type,
        first_registration_date: vehicle.first_registration_date,
        acquisition_date: vehicle.acquisition_date,
        vin: vehicle.vin,
        notes: vehicle.notes,
      });
    } else {
      form.reset({
        license_plate: "",
        brand: "",
        model: "",
        category: "vl",
        fuel_type: "gasoline",
        acquisition_type: "owned",
        first_registration_date: null,
        acquisition_date: null,
        vin: null,
        notes: null,
      });
    }
    // Reset scan state when dialog opens/closes
    setScanState("idle");
    setScannedFile(null);
    setUploadedFilePath(null);
    setAutoFilledFields(new Set());
  }, [vehicle, form, open]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setScanState("scanning");
    setScannedFile(file);

    try {
      console.log("[VehicleDialog] Starting gray card scan...");
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error("[VehicleDialog] Auth error:", authError);
        throw new Error(`Erreur auth: ${authError.message}`);
      }
      
      if (!user) {
        throw new Error("Non authentifié - veuillez vous reconnecter");
      }
      console.log("[VehicleDialog] User authenticated:", user.id);

      // Upload directly to permanent location
      const filePath = `${user.id}/gray-cards/${Date.now()}-${file.name}`;
      console.log("[VehicleDialog] Uploading to:", filePath);
      
      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, file);

      if (uploadError) {
        console.error("[VehicleDialog] Upload error:", uploadError);
        throw new Error(`Erreur upload: ${uploadError.message}`);
      }
      console.log("[VehicleDialog] Upload success");
      setUploadedFilePath(filePath);

      // Call extract-document-info with gray_card type
      console.log("[VehicleDialog] Calling extract-document-info...");
      const { data, error } = await supabase.functions.invoke("extract-document-info", {
        body: {
          fileName: file.name,
          mimeType: file.type,
          filePath,
          document_type: "gray_card"
        }
      });

      console.log("[VehicleDialog] Edge function response:", { data, error });

      if (error) {
        console.error("[VehicleDialog] Edge function error:", error);
        throw new Error(`Erreur extraction: ${error.message || JSON.stringify(error)}`);
      }

      if (data?.error) {
        console.warn("[VehicleDialog] Extraction returned error:", data.error);
        throw new Error(`Extraction échouée: ${data.error}`);
      }

      // Map extracted data to form fields
      const filledFields = new Set<string>();

      if (data?.license_plate) {
        form.setValue("license_plate", data.license_plate);
        filledFields.add("license_plate");
      }
      if (data?.brand) {
        form.setValue("brand", data.brand);
        filledFields.add("brand");
      }
      if (data?.model) {
        form.setValue("model", data.model);
        filledFields.add("model");
      }
      if (data?.vin) {
        form.setValue("vin", data.vin);
        filledFields.add("vin");
      }
      if (data?.first_registration_date) {
        form.setValue("first_registration_date", data.first_registration_date);
        filledFields.add("first_registration_date");
      }
      if (data?.fuel_type && ["gasoline", "diesel", "electric", "hybrid", "lpg", "other"].includes(data.fuel_type)) {
        form.setValue("fuel_type", data.fuel_type);
        filledFields.add("fuel_type");
      }
      if (data?.category && ["vl", "vu", "pl", "two_wheels", "other"].includes(data.category)) {
        form.setValue("category", data.category);
        filledFields.add("category");
      }

      console.log("[VehicleDialog] Filled fields:", [...filledFields]);
      setAutoFilledFields(filledFields);
      setScanState("success");
      toast.success("Données extraites par IA", {
        description: `${filledFields.size} champ(s) pré-rempli(s)`,
        icon: <Sparkles className="h-4 w-4" />
      });

    } catch (err: any) {
      console.error("[VehicleDialog] Scan error:", err);
      setScanState("error");
      
      const errorMessage = err?.message || err?.error || String(err) || "Erreur inconnue";
      toast.error("Erreur lors de l'analyse", {
        description: errorMessage.length > 100 
          ? errorMessage.substring(0, 100) + "..." 
          : errorMessage
      });
    }
  }, [form]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".webp", ".heic"],
      "application/pdf": [".pdf"]
    },
    maxSize: 10 * 1024 * 1024, // 10 MB
    multiple: false,
    disabled: scanState === "scanning"
  });

  const resetScan = () => {
    setScanState("idle");
    setScannedFile(null);
    setUploadedFilePath(null);
    setAutoFilledFields(new Set());
  };

  const handleViewScannedDocument = async () => {
    if (!uploadedFilePath || !scannedFile) return;
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Create a temporary document object for the viewer
    const tempDoc = {
      id: "temp-gray-card",
      user_id: user.id,
      title: `Carte grise - ${form.getValues("license_plate") || scannedFile.name}`,
      file_name: scannedFile.name,
      file_path: uploadedFilePath,
      mime_type: scannedFile.type,
      bucket_id: "documents",
      doc_type: "gray_card" as const,
      source: "upload" as const,
      status: "complete" as const,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    
    openDocument(tempDoc as any);
  };

  const handleSubmit = async (values: VehicleFormValues) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      let grayCardDocumentId: string | null = null;

      // If we have a scanned file and user wants to save it, create a document first
      if (saveDocument && scannedFile && uploadedFilePath) {
        console.log("[VehicleDialog] Creating document record for gray card...");
        
        const { data: docData, error: docError } = await supabase
          .from("documents")
          .insert([{
            user_id: user.id,
            title: `Carte grise - ${values.license_plate}`,
            file_name: scannedFile.name,
            file_path: uploadedFilePath,
            mime_type: scannedFile.type,
            file_size: scannedFile.size,
            doc_type: "gray_card" as const,
            source: "upload" as const,
            status: "complete" as const,
            category: "vehicle" as const,
            bucket_id: "documents",
          }])
          .select("id")
          .single();

        if (docError) {
          console.error("[VehicleDialog] Document creation error:", docError);
          toast.error("Erreur lors de la sauvegarde du document");
        } else if (docData) {
          grayCardDocumentId = docData.id;
          console.log("[VehicleDialog] Document created with ID:", grayCardDocumentId);
        }
      }

      // Create vehicle with optional gray card document link
      await onSubmit({
        ...values,
        status: vehicle?.status || "active",
        organization_id: null,
        gray_card_document_id: grayCardDocumentId,
      } as VehicleInsert);

      if (grayCardDocumentId) {
        toast.success("Véhicule et carte grise enregistrés", {
          description: "Le scan de la carte grise a été archivé et lié au véhicule"
        });
      }

      onOpenChange(false);
    } catch (err: any) {
      console.error("[VehicleDialog] Submit error:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAutoFillBadge = (fieldName: string) => {
    if (!autoFilledFields.has(fieldName)) return null;
    return (
      <span className="ml-2 inline-flex items-center gap-1 text-xs text-primary">
        <Sparkles className="h-3 w-3" />
        IA
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {vehicle ? "Modifier le véhicule" : "Ajouter un véhicule"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            
            {/* Scan Zone - only for new vehicles */}
            {!vehicle && (
              <>
                <div
                  {...getRootProps()}
                  className={`
                    border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer
                    ${isDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"}
                    ${scanState === "scanning" ? "pointer-events-none opacity-75" : ""}
                    ${scanState === "success" ? "border-green-500 bg-green-50 dark:bg-green-950/20" : ""}
                    ${scanState === "error" ? "border-destructive bg-destructive/5" : ""}
                  `}
                >
                  <input {...getInputProps()} />
                  
                  {scanState === "idle" && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-3 rounded-full bg-muted">
                        <Scan className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="font-medium text-sm">Scanner la carte grise</p>
                      <p className="text-xs text-muted-foreground">
                        Glissez-déposez ou cliquez pour remplir automatiquement
                      </p>
                      <p className="text-xs text-muted-foreground/70">
                        JPG, PNG, PDF • Max 10 Mo
                      </p>
                    </div>
                  )}

                  {scanState === "scanning" && (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <p className="font-medium text-sm">Analyse de la carte grise...</p>
                      <p className="text-xs text-muted-foreground">Cela peut prendre quelques secondes</p>
                    </div>
                  )}

                  {scanState === "success" && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                        <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <p className="font-medium text-sm flex items-center gap-1.5 text-green-700 dark:text-green-400">
                        <Sparkles className="h-4 w-4" />
                        Données extraites par IA
                      </p>
                      {scannedFile && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <FileText className="h-3 w-3" />
                          <span className="max-w-[150px] truncate">{scannedFile.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 p-0 hover:bg-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewScannedDocument();
                            }}
                            title="Voir le document scanné"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); resetScan(); }}
                        className="mt-1"
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1" />
                        Scanner un autre
                      </Button>
                    </div>
                  )}

                  {scanState === "error" && (
                    <div className="flex flex-col items-center gap-2">
                      <div className="p-2 rounded-full bg-destructive/10">
                        <Scan className="h-6 w-6 text-destructive" />
                      </div>
                      <p className="font-medium text-sm text-destructive">Échec de l'analyse</p>
                      <p className="text-xs text-muted-foreground">Cliquez pour réessayer</p>
                    </div>
                  )}
                </div>

                {/* Save document checkbox */}
                {scannedFile && scanState === "success" && (
                  <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
                    <Checkbox 
                      id="save-gray-card" 
                      checked={saveDocument}
                      onCheckedChange={(checked) => setSaveDocument(checked === true)}
                    />
                    <label 
                      htmlFor="save-gray-card" 
                      className="text-sm cursor-pointer flex items-center gap-2"
                    >
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      Sauvegarder ce scan comme document "Carte grise"
                    </label>
                  </div>
                )}

                {/* Separator */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      ou remplir manuellement
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* License plate */}
            <FormField
              control={form.control}
              name="license_plate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    Immatriculation *
                    {renderAutoFillBadge("license_plate")}
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="AA-123-BB" 
                      {...field} 
                      className="font-mono uppercase"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Brand + Model in row */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="brand"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Marque *
                      {renderAutoFillBadge("brand")}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Renault" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="model"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Modèle *
                      {renderAutoFillBadge("model")}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Clio" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Category + Fuel type */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Catégorie *
                      {renderAutoFillBadge("category")}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(VEHICLE_CATEGORIES).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="fuel_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Énergie
                      {renderAutoFillBadge("fuel_type")}
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(FUEL_TYPES).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Acquisition type */}
            <FormField
              control={form.control}
              name="acquisition_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mode d'acquisition</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(ACQUISITION_TYPES).map(([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_registration_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center">
                      Date 1ère immatriculation
                      {renderAutoFillBadge("first_registration_date")}
                    </FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        value={field.value || ""} 
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="acquisition_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date d'acquisition</FormLabel>
                    <FormControl>
                      <Input 
                        type="date" 
                        {...field} 
                        value={field.value || ""} 
                        onChange={(e) => field.onChange(e.target.value || null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* VIN */}
            <FormField
              control={form.control}
              name="vin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center">
                    Numéro VIN (optionnel)
                    {renderAutoFillBadge("vin")}
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="VF1XXXXXXXXX" 
                      {...field} 
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                      className="font-mono uppercase"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (optionnel)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Informations complémentaires..." 
                      {...field} 
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {vehicle ? "Enregistrer" : "Ajouter"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
